import { IEntity, IMultipleEntities, IQuery, IQueryMultiple, IQueryOptions, QueryType } from "../../../base/types";
import { IFieldValue, IReadMultipleOptions } from "../../../services/models";
import { useCreateMutation, useLazyReadMultipleQuery, useLazyReadQuery, useUpdateMutation } from "../../../services/data/tableDataApi";
import { useEffect, useState } from "react";

import { IFilterGroup } from "../../../data";
import { useAuth } from "../../../../modules/auth";
import { useDataSource } from "./DataSourceProvider";
import { useParams } from "react-router-dom";

export const useTableDataQuery = () => {
    const [readRecord, { data: record }] = useLazyReadQuery();
    const [readRecords, { data: records }] = useLazyReadMultipleQuery();
    const [insertRecord] = useCreateMutation();
    const [modifyRecord] = useUpdateMutation();
    const [data, setData] = useState<IEntity | IMultipleEntities | undefined>()

    const [queryOptions, setQueryOptions] = useState<IQueryOptions>()
    const { dataSource } = useDataSource()
    const { user } = useAuth()
    const { siteId } = useParams()

    useEffect(() => {
        setData(record)
        // eslint-disable-next-line
    }, [record])

    useEffect(() => {
        setData(records)
        // eslint-disable-next-line
    }, [records])

    const getRecord = (query: IQuery, preferCacheValue: boolean = true) => {
        if (!siteId || !dataSource?.entity)
            return Promise.reject()

        const { id: sysId, fields: fieldList } = query;
        const requestData = {
            userId: user?.userId,
            portalId: siteId,
            tableId: dataSource.entity,
            sysId,
            fieldList
        };

        setQueryOptions({ query, queryType: QueryType.Record })
        return readRecord(requestData, preferCacheValue)
            .unwrap();
    };

    const getFirstRecord = (query: IQueryMultiple, preferCacheValue: boolean = true) => {
        if (!siteId || !dataSource?.entity)
            return Promise.reject()

        const { fields: fieldList, filters, orderBy } = query;
        const request: IReadMultipleOptions = {
            userId: user?.userId,
            portalId: siteId,
            tableId: dataSource.entity,
            fieldList,
            filters,
            pageNo: 0,
            recordLimit: 1,
            orderBy,
        };

        setQueryOptions({ query, queryType: QueryType.FirstRecord })
        return readRecords(request, preferCacheValue)
            .unwrap()
            .then(data => {
                const records = data?.records || [];
                const record = Array.isArray(records) ? (records[0] || undefined) : records;
                return Promise.resolve(record)
            })
    };

    const getMultipleRecords = (query: IQueryMultiple, preferCacheValue: boolean = false) => {
        if (!siteId || !dataSource?.entity)
            return Promise.reject()

        const { fields: fieldList, filters, pageNo, pageSize, orderBy } = query;
        const request: IReadMultipleOptions = {
            userId: user?.userId,
            portalId: siteId,
            tableId: dataSource.entity,
            fieldList,
            filters,
            pageNo: pageNo || 0,
            recordLimit: pageSize,
            orderBy,
        };

        setQueryOptions({ query, queryType: QueryType.MultipleRecords })
        return readRecords(request, preferCacheValue)
            .unwrap()
    };

    const refetch = () => {
        if (!queryOptions) return Promise.reject();
        const { query, queryType } = queryOptions

        switch (queryType) {
            case QueryType.Record:
                return getRecord(query, false);
            case QueryType.FirstRecord:
                return getFirstRecord(query, false);
            case QueryType.MultipleRecords:
                return getMultipleRecords(query, false);
        }
    };

    const fetch = (queryOptions: IQueryOptions) => {
        if (!queryOptions)
            return Promise.reject();

        const { query, queryType } = queryOptions

        switch (queryType) {
            case QueryType.Record:
                return getRecord(query);
            case QueryType.FirstRecord:
                return getFirstRecord(query);
            case QueryType.MultipleRecords:
                return getMultipleRecords(query);
        }
    }

    const goto = (pageNo: number) => {
        if (queryOptions?.queryType !== QueryType.MultipleRecords)
            return Promise.reject();

        return getMultipleRecords({ ...queryOptions.query, pageNo });
    }

    const saveData = ({ values, systemId }: { values: IEntity, systemId?: string }): Promise<IEntity> => {
        if (!user || !dataSource || !siteId)
            return Promise.reject();

        const getFilters = () => {
            switch (queryOptions?.queryType) {
                case QueryType.Record:
                    return []
                case QueryType.FirstRecord:
                case QueryType.MultipleRecords:
                    return queryOptions.query.filters || []
                default:
                    return []
            }
        }

        const { userId } = user
        const tableId = dataSource.entity

        const data: IFieldValue[] = []
        for (var field in values) {
            if (values[field] !== undefined)
                data.push({ field, value: values[field], validate: true })
        }

        const filters: IFilterGroup[] = getFilters()

        if (!systemId) {
            return insertRecord({ portalId: siteId, userId, tableId, filters, data, triggers: true })
                .unwrap()
                .catch(err => {
                    const message = err.data.message || 'An error occurred while processing your request. Please try again later.';
                    return Promise.reject(message)
                })
        } else {
            return modifyRecord({ portalId: siteId, userId, tableId, sysId: systemId, filters, data, triggers: true })
                .unwrap()
                .catch(err => {
                    const message = err.data.message || 'An error occurred while processing your request. Please try again later.';
                    return Promise.reject(message)
                })
        }
    }

    return { queryOptions, fetch, refetch, goto, saveData, data }
}
